import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Slider,
  Button,
  LinearProgress,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Chip,
  Collapse
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import ToggleCard from '../components/ToggleCard';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

const Loyalty: React.FC = () => {
  type ImpactOption = 'mangroves' | 'education' | 'plastic';

  const availablePoints = 2500;
  const [sent, setSent] = useState(false);
  const [convertPoints, setConvertPoints] = useState(800);
  const [impactOption, setImpactOption] = useState<ImpactOption>('mangroves');
  const [autoConvert, setAutoConvert] = useState(false);
  const theme = useTheme();
  const confirmRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const handleSend = () => setSent(true);

  const conversionRates: Record<ImpactOption, number> = {
    mangroves: 250,
    education: 50,
    plastic: 50
  };

  const impactLabels: Record<ImpactOption, { label: string; unit: string; emoji: string, description: string }> = {
    mangroves: { emoji: '🌱', label: 'Restore Mangroves in Indonesia', unit: 'mangroves restored', description: 'Restore coastal ecosystems' },
    education: { emoji: '📚', label: "Fund a Child's Education in Cambodia", unit: 'days', description: 'Support sustainable community development' },
    plastic: { emoji: '🌊', label: 'Clean up ocean plastic', unit: 'kg of plastic', description: 'Remove plastics from the oceans' },
  };

  const impactDescriptions: Record<ImpactOption, string> = {
    mangroves: 'Restores coastal ecosystems',
    education: 'Support sustainable community development',
    plastic: 'Clean up ocean plastic'
  };

  const points = {
    pointsAvailable: 2500,
    pointsToExpire: 500
  };

  const impactSummary = [
    {
      "label": "Mangroves restored",
      "value": 15
    },
    {
      "label": "Days of School",
      "value": 3
    },
    {
      "label": "Plastic removed",
      "value": 75,
      "unit": "kg"
    }
  ]

  const impactAmount = Math.floor(convertPoints / conversionRates[impactOption]);

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 2 }}>Loyalty</Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }} color="text.primary">
        Convert your loyalty points into direct environmental and social impact with our API
      </Typography>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={4}>
        {/* Left Panel */}
        <Box flex={1}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', mb: 3, fontWeight: 700 }}>
                <EmojiEventsIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Loyalty Points
              </Typography>

              {/* Status Box */}
              <ToggleCard code={{ points }} title="&nbsp;" sx={{ border: '0px', mt: '-20px', mb: '-30px' }}>
                <Box sx={{ backgroundColor: 'rgba(239,253,219,0.6)', borderRadius: 2, p: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Your Loyalty Status</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>{availablePoints.toLocaleString()} available points</Typography>
                  <LinearProgress variant="determinate" value={(availablePoints / 5000) * 100} sx={{ height: 10, borderRadius: 5, mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2"></Typography>
                    <Button size="small" startIcon={<HistoryIcon />}>View History</Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Points expiring this month: {points.pointsToExpire}</Typography>
                </Box>
              </ToggleCard>

              {/* Convert Slider */}
              <Typography sx={{ mb: 1 }}>Points to Convert</Typography>
              <Typography sx={{ mb: 2, fontWeight: 600, textAlign: 'right' }}>{convertPoints} pts</Typography>
              <Slider
                value={convertPoints}
                min={100}
                max={availablePoints}
                step={50}
                onChange={(e, val) => setConvertPoints(val as number)}
                valueLabelDisplay="auto"
                disabled={sent}
                sx={{ color: 'rgb(101,183,65)' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', mt: 1, mb: 3 }}>
                <span>100 pts</span>
                <span>{availablePoints.toLocaleString()} pts</span>
              </Box>


              {/* Choose Impact */}
              <ToggleCard title="Choose Impact Project" code={{ impact: impactLabels }}>
                {(['mangroves', 'education', 'plastic'] as ImpactOption[]).map(key => (
                  <Box
                    onClick={() => !sent && setImpactOption(key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      backgroundColor: impactOption === key ? 'rgba(101,183,65,0.1)' : '#fff',
                      p: 1,
                      mb: 1,
                      borderRadius: 1
                    }}
                  >
                    {impactOption === key ? (
                      <RadioButtonCheckedIcon sx={{ color: 'rgb(101,183,65)', mr: 1 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: 'rgb(101,183,65)', mr: 1 }} />
                    )}
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ fontSize: 20, mr: 1 }}>{impactLabels[key].emoji}</Typography>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{impactLabels[key].label}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgb(117,117,117)' }}>{impactLabels[key].description}</Typography>
                        <Typography variant="body2" color="text.secondary">{conversionRates[key]} pts = 1 {impactLabels[key].unit}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </ToggleCard>

              {/* Auto-convert switch */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 4 }}>
                <FormControlLabel
                  control={<Switch checked={autoConvert} onChange={() => setAutoConvert(!autoConvert)} color="primary" disabled={sent} />}
                  label="Auto-convert expiring points"
                />
                <Chip label="Monthly" size="small" sx={{ ml: 'auto', bgcolor: 'rgb(239,253,219)', color: 'rgb(101,183,65)', fontWeight: 600 }} />
              </Box>

              <Button variant="contained" color="primary" fullWidth sx={{ py: 1.5, fontWeight: 700 }}
                onClick={handleSend}>
                Convert Points to Impact
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Right Panel */}
        <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '80px' }}>
          <Collapse
            in={sent}
            timeout={600}
            unmountOnExit
            onEntered={() => confirmRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', mb: 3, fontWeight: 700, color: 'rgb(79,111,82)' }}>
                  <EmojiEventsIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Your Loyalty Impact
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Your points have been redeemed! See how you are making a difference.
                </Typography>

                <ToggleCard title="&nbsp;" code={{ impact: impactSummary }} sx={{ border: '0px' }}>
                  {/* Impact Summary */}
                  <Box sx={{ backgroundColor: 'rgba(239,253,219,0.6)', borderRadius: 2, p: 3, textAlign: 'center', mb: 3, border: '1px solid rgb(209,230,218)' }}>
                    <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                      <Typography sx={{ fontSize: 24 }}>{impactLabels[impactOption].emoji}</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: 'rgb(79,111,82)', fontWeight: 700, mb: 1 }}>
                      {impactAmount} {impactLabels[impactOption].unit}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {impactDescriptions[impactOption]}
                    </Typography>
                  </Box>

                  {/* Total Contribution Boxes */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>15</Typography>
                      <Typography variant="body2">Mangroves restored</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>3</Typography>
                      <Typography variant="body2">Days of School</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>75kg</Typography>
                      <Typography variant="body2">Plastic removed</Typography>
                    </Box>
                  </Box>
                </ToggleCard>
                <Box
                  component={Link}
                  to="/engagement"
                  sx={{
                    textDecoration: 'none',     // remove underline
                    color: 'inherit',           // inherit text colour
                    backgroundColor: 'rgb(239,253,219)',
                    p: 2,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgb(227,244,204)',
                      textDecoration: 'none'    // ensure no underline on hover
                    }
                  }}
                >
                  <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                  <Typography>{'→'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      </Box>
    </Container>
  );
};

export default Loyalty; 